// Phase SE-8 — Slip Extractor Section C history view.
//
// Per SlipExtractor_Build_Plan_v1.3 §7 SE-8 + §6 (3-section shell):
//   • Lists past extractions for the signed-in user (max 100 rows).
//   • Filters: free-text search + status + mode + date range.
//   • Click-to-expand shows the full prompt, plan, kept/dropped legs,
//     and N09 decline reason where applicable.
//   • CSV export for personal record-keeping.
//
// N04: empty state is meaningful, not blank.
// N01: fetch failures soft-fail to empty; never surface raw errors.

import { useMemo, useState } from "react";

import {
  CSV_HEADER,
  EMPTY_FILTER,
  filterExtractionRows,
  relativeTime,
  summariseRow,
  toCsvRow,
  type ExtractionHistoryFilter,
} from "../../extractor/extractionHistoryHelpers.ts";
import {
  useExtractionHistory,
  type ExtractionHistoryRow,
} from "../../extractor/useExtractionHistory.ts";

// R6 — design chrome (replaces the old sa-form-input / sa-add-leg-btn
// classes). se-* tokenised; zero Autopsy chrome dependency.
const seInput: React.CSSProperties = {
  padding: "9px 12px",
  background: "var(--se-surface-page)",
  border: "1px solid var(--se-border)",
  borderRadius: 2,
  color: "var(--se-text)",
  fontFamily: "var(--se-font-body)",
  fontSize: 13,
  outline: "none",
};
const seBtnSecondary: React.CSSProperties = {
  padding: "9px 16px",
  border: "1px solid var(--se-border)",
  background: "transparent",
  color: "var(--se-text-sec)",
  fontFamily: "var(--se-font-body)",
  fontSize: 13,
  fontWeight: 500,
  borderRadius: 2,
  cursor: "pointer",
};

export function ExtractionHistory(): JSX.Element {
  const { rows, loading } = useExtractionHistory();
  const [filter, setFilter] = useState<ExtractionHistoryFilter>(EMPTY_FILTER);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const filtered = useMemo(
    () => filterExtractionRows(rows, filter),
    [rows, filter],
  );

  function toggle(id: string): void {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function downloadCsv(): void {
    const csv = [CSV_HEADER, ...filtered.map(toCsvRow)].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `strikza-extractions-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  if (loading) {
    return (
      <div
        data-testid="sle-history-loading"
        style={{
          padding: 16,
          color: "var(--se-text-muted)",
          fontFamily: "var(--se-font-mono)",
          fontSize: 11,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
        }}
      >
        Loading history…
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div
        data-testid="sle-history-empty"
        style={{
          padding: "24px 20px",
          border: "1px dashed var(--se-border)",
          borderRadius: 4,
          background: "var(--se-surface-raised)",
          color: "var(--se-text-sec)",
          fontFamily: "var(--se-font-body)",
          fontSize: 13.5,
          lineHeight: 1.55,
        }}
      >
        No extractions yet. Run one above — your past extractions land
        here with the prompt, the plan, the kept legs, and the live CLV
        trail once those legs resolve.
      </div>
    );
  }

  return (
    <div
      data-testid="sle-history"
      style={{ display: "flex", flexDirection: "column", gap: 12 }}
    >
      <ExtractionHistoryFilters
        filter={filter}
        onChange={setFilter}
        onExport={downloadCsv}
        canExport={filtered.length > 0}
      />

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          fontFamily: "var(--se-font-mono)",
          fontSize: 11,
          letterSpacing: "0.12em",
          color: "var(--se-text-muted)",
        }}
      >
        <span data-testid="sle-history-count">
          Showing {filtered.length} of {rows.length}
        </span>
      </div>

      {filtered.length === 0 ? (
        <div
          data-testid="sle-history-no-matches"
          style={{
            padding: 16,
            color: "var(--se-text-sec)",
            fontFamily: "var(--se-font-body)",
            fontSize: 13,
          }}
        >
          No extractions match the current filters.
        </div>
      ) : (
        filtered.map((row) => (
          <ExtractionHistoryRowCard
            key={row.extraction_id}
            row={row}
            expanded={expanded.has(row.extraction_id)}
            onToggle={() => toggle(row.extraction_id)}
          />
        ))
      )}
    </div>
  );
}

// ─── Filter bar ────────────────────────────────────────────────

interface FiltersProps {
  filter: ExtractionHistoryFilter;
  onChange: (f: ExtractionHistoryFilter) => void;
  onExport: () => void;
  canExport: boolean;
}

function ExtractionHistoryFilters({
  filter,
  onChange,
  onExport,
  canExport,
}: FiltersProps): JSX.Element {
  return (
    <div
      data-testid="sle-history-filters"
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
        gap: 8,
        alignItems: "end",
      }}
    >
      <label
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 4,
          fontFamily: "var(--se-font-mono)",
          fontSize: 10.5,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          color: "var(--se-text-muted)",
        }}
      >
        Search
        <input
          data-testid="sle-history-filter-query"
          type="text"
          value={filter.query}
          onChange={(e) => onChange({ ...filter, query: e.target.value })}
          placeholder="Search prompts + summaries"
          style={{
            ...seInput,
            fontFamily: "var(--se-font-body)",
            fontSize: 13,
          }}
        />
      </label>
      <label
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 4,
          fontFamily: "var(--se-font-mono)",
          fontSize: 10.5,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          color: "var(--se-text-muted)",
        }}
      >
        Status
        <select
          data-testid="sle-history-filter-status"
          value={filter.status}
          onChange={(e) =>
            onChange({ ...filter, status: e.target.value as any })
          }
          style={seInput}
        >
          <option value="all">All</option>
          <option value="complete">Complete</option>
          <option value="declined">Declined</option>
          <option value="queued">Queued</option>
          <option value="processing">Processing</option>
        </select>
      </label>
      <label
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 4,
          fontFamily: "var(--se-font-mono)",
          fontSize: 10.5,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          color: "var(--se-text-muted)",
        }}
      >
        Mode
        <select
          data-testid="sle-history-filter-mode"
          value={filter.mode}
          onChange={(e) =>
            onChange({ ...filter, mode: e.target.value as any })
          }
          style={seInput}
        >
          <option value="all">All</option>
          <option value="upload">Upload</option>
          <option value="paste">Paste</option>
        </select>
      </label>
      <label
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 4,
          fontFamily: "var(--se-font-mono)",
          fontSize: 10.5,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          color: "var(--se-text-muted)",
        }}
      >
        From
        <input
          data-testid="sle-history-filter-from"
          type="date"
          value={filter.dateFrom ?? ""}
          onChange={(e) =>
            onChange({ ...filter, dateFrom: e.target.value || null })
          }
          style={seInput}
        />
      </label>
      <label
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 4,
          fontFamily: "var(--se-font-mono)",
          fontSize: 10.5,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          color: "var(--se-text-muted)",
        }}
      >
        To
        <input
          data-testid="sle-history-filter-to"
          type="date"
          value={filter.dateTo ?? ""}
          onChange={(e) =>
            onChange({ ...filter, dateTo: e.target.value || null })
          }
          style={seInput}
        />
      </label>
      <button
        type="button"
        data-testid="sle-history-export-csv"
        onClick={onExport}
        disabled={!canExport}
        style={{
          ...seBtnSecondary,
          alignSelf: "flex-end",
          opacity: canExport ? 1 : 0.5,
        }}
      >
        Export CSV
      </button>
    </div>
  );
}

// ─── Row card ──────────────────────────────────────────────────

interface RowCardProps {
  row: ExtractionHistoryRow;
  expanded: boolean;
  onToggle: () => void;
}

function ExtractionHistoryRowCard({
  row,
  expanded,
  onToggle,
}: RowCardProps): JSX.Element {
  const statusColor = statusToColor(row.status);
  const summary = summariseRow(row);

  return (
    <div
      data-testid={`sle-history-row-${row.extraction_id}`}
      data-status={row.status}
      style={{
        border: "1px solid var(--se-border)",
        borderLeft: `2px solid ${statusColor}`,
        borderRadius: 4,
        background: "var(--se-surface-raised)",
        overflow: "hidden",
      }}
    >
      {/* Header — always visible */}
      <button
        type="button"
        onClick={onToggle}
        data-testid={`sle-history-row-${row.extraction_id}-toggle`}
        style={{
          display: "grid",
          gridTemplateColumns: "1fr auto auto",
          gap: 12,
          width: "100%",
          padding: "12px 14px",
          background: "transparent",
          border: "none",
          textAlign: "left",
          cursor: "pointer",
          color: "var(--se-text)",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span
            style={{
              fontFamily: "var(--se-font-body)",
              fontSize: 13.5,
              fontWeight: 500,
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
            }}
          >
            {row.user_prompt}
          </span>
          <span
            style={{
              fontFamily: "var(--se-font-mono)",
              fontSize: 10.5,
              letterSpacing: "0.10em",
              color: "var(--se-text-muted)",
            }}
          >
            {summary}
          </span>
        </div>
        <span
          style={{
            fontFamily: "var(--se-font-mono)",
            fontSize: 10,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            color: statusColor,
            alignSelf: "center",
          }}
        >
          {row.status}
        </span>
        <span
          style={{
            fontFamily: "var(--se-font-mono)",
            fontSize: 10.5,
            color: "var(--se-text-muted)",
            alignSelf: "center",
          }}
        >
          {relativeTime(row.created_at)}
        </span>
      </button>

      {/* Body — expanded */}
      {expanded && (
        <div
          data-testid={`sle-history-row-${row.extraction_id}-body`}
          style={{
            padding: "12px 14px",
            borderTop: "1px solid var(--se-border)",
            display: "flex",
            flexDirection: "column",
            gap: 10,
            fontFamily: "var(--se-font-body)",
            fontSize: 13,
            color: "var(--se-text-sec)",
            lineHeight: 1.5,
          }}
        >
          {/* Plan summary */}
          {row.plan_summary && (
            <div>
              <div
                style={{
                  fontFamily: "var(--se-font-mono)",
                  fontSize: 10,
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  color: "var(--se-text-muted)",
                  marginBottom: 4,
                }}
              >
                Plan
              </div>
              <div data-testid={`sle-history-row-${row.extraction_id}-plan`}>
                {row.plan_summary}
              </div>
            </div>
          )}

          {/* Decline reason */}
          {row.declined_reason && (
            <div
              data-testid={`sle-history-row-${row.extraction_id}-decline`}
              style={{
                padding: "10px 12px",
                border: "1px solid var(--se-conflict-amber)",
                borderLeft: "2px solid var(--se-amber)",
                borderRadius: 4,
                background: "var(--se-surface-card)",
                color: "var(--se-text)",
              }}
            >
              <strong>Declined:</strong> {row.declined_reason}
            </div>
          )}

          {/* Aggregate stats */}
          {row.status === "complete" && (
            <div
              data-testid={`sle-history-row-${row.extraction_id}-stats`}
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))",
                gap: 10,
                fontFamily: "var(--se-font-mono)",
                fontSize: 11,
              }}
            >
              <Stat label="Legs kept" value={`${row.selected_leg_indices.length} / ${Array.isArray(row.scored_legs) ? row.scored_legs.length : 0}`} />
              <Stat
                label="Combined odds"
                value={row.combined_odds_kept === null ? "—" : row.combined_odds_kept.toFixed(2)}
              />
              <Stat
                label="Hit probability"
                value={row.hit_probability_kept === null ? "—" : `${(row.hit_probability_kept * 100).toFixed(1)}%`}
              />
              <Stat
                label="EV"
                value={row.expected_value_kept === null ? "—" : `${row.expected_value_kept >= 0 ? "+" : ""}${(row.expected_value_kept * 100).toFixed(1)}%`}
                positive={row.expected_value_kept !== null && row.expected_value_kept > 0}
              />
            </div>
          )}

          {/* The actual games — kept first, then dropped. This is the
              user's own private record (RLS by user_id), so showing the
              full slip here is fine. */}
          <LegBreakdown row={row} />
        </div>
      )}
    </div>
  );
}

// ─── Kept / dropped games for an expanded history row ───────────

interface HistLeg {
  fixture: string;
  market: string;
  selection: string;
  odds: number | null;
}

function normaliseHistLeg(l: unknown): HistLeg {
  const o = (l ?? {}) as Record<string, unknown>;
  const fixture = typeof o.fixture === "string" && o.fixture.length > 0
    ? o.fixture
    : [o.home_team, o.away_team].filter(Boolean).join(" v ") || "—";
  return {
    fixture,
    market: typeof o.market === "string" ? o.market : "?",
    selection: typeof o.selection === "string" ? o.selection : "?",
    odds: typeof o.odds === "number" ? o.odds : null,
  };
}

function LegBreakdown({ row }: { row: ExtractionHistoryRow }): JSX.Element | null {
  const scored = Array.isArray(row.scored_legs) ? row.scored_legs : [];
  if (scored.length === 0) return null;
  const keptIdx = new Set(
    Array.isArray(row.selected_leg_indices) ? row.selected_leg_indices : [],
  );
  const kept: HistLeg[] = [];
  const dropped: HistLeg[] = [];
  scored.forEach((l, i) => {
    (keptIdx.has(i) ? kept : dropped).push(normaliseHistLeg(l));
  });

  return (
    <div
      data-testid={`sle-history-row-${row.extraction_id}-legs`}
      style={{ display: "flex", flexDirection: "column", gap: 12 }}
    >
      {kept.length > 0 && (
        <LegList label={`Kept · ${kept.length}`} legs={kept} accent="var(--se-keep-green)" />
      )}
      {dropped.length > 0 && (
        <LegList label={`Dropped · ${dropped.length}`} legs={dropped} accent="var(--se-drop-red)" muted />
      )}
    </div>
  );
}

function LegList({
  label,
  legs,
  accent,
  muted,
}: {
  label: string;
  legs: HistLeg[];
  accent: string;
  muted?: boolean;
}): JSX.Element {
  return (
    <div>
      <div
        style={{
          fontFamily: "var(--se-font-mono)",
          fontSize: 10,
          letterSpacing: "0.16em",
          textTransform: "uppercase",
          color: accent,
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {legs.map((l, i) => (
          <div
            key={i}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
              gap: 12,
              padding: "6px 8px",
              borderRadius: 3,
              background: "var(--se-surface-card)",
              opacity: muted ? 0.72 : 1,
            }}
          >
            <span style={{ minWidth: 0 }}>
              <span
                style={{
                  fontFamily: "var(--se-font-body)",
                  fontSize: 12.5,
                  color: "var(--se-text)",
                  fontWeight: 500,
                }}
              >
                {l.fixture}
              </span>
              <span
                style={{
                  fontFamily: "var(--se-font-mono)",
                  fontSize: 10.5,
                  color: "var(--se-text-muted)",
                  marginLeft: 8,
                }}
              >
                {l.market} · {l.selection}
              </span>
            </span>
            <span
              style={{
                fontFamily: "var(--se-font-mono)",
                fontSize: 12,
                color: "var(--se-text-sec)",
                whiteSpace: "nowrap",
              }}
            >
              {l.odds === null ? "—" : `@ ${l.odds.toFixed(2)}`}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  positive,
}: {
  label: string;
  value: string;
  positive?: boolean;
}): JSX.Element {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <span
        style={{
          fontSize: 9.5,
          letterSpacing: "0.16em",
          textTransform: "uppercase",
          color: "var(--se-text-muted)",
        }}
      >
        {label}
      </span>
      <span
        style={{
          color: positive ? "var(--se-keep-green)" : "var(--se-text)",
          fontWeight: 600,
        }}
      >
        {value}
      </span>
    </div>
  );
}

function statusToColor(status: ExtractionHistoryRow["status"]): string {
  switch (status) {
    case "complete":
      return "var(--se-keep-green)";
    case "declined":
      return "var(--se-amber)";
    case "queued":
    case "processing":
      return "var(--se-text-muted)";
    case "failed":
      return "var(--se-drop-red)";
  }
}
